import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useContext, useEffect, useState } from 'react';
import { number, object, ObjectSchema, string } from 'yup';
import ClientOnboardingApi from '../../../../apis/ClientOnboardingApi';
import FormikEnumSelect from '../../../../components/FormikEnumSelect/FormikEnumSelect';
import FormikTextField from '../../../../components/FormikTextField/FormikTextField';
import LoadingCircle from '../../../../components/LoadingCircle/LoadingCircle';
import SnackbarContext from '../../../../contexts/SnackbarContext';
import { BasicInformationDto } from '../../../../generated-types/basicInformationDto';
import { UsState } from '../../../../generated-types/usState';
import useApi from '../../../../lib/useApi';
import CommonStepProps from '../../lib/CommonStepProps';

/**
 * The basic information form dto modified for the form
 */
type BasicInformationFormDto = Omit<BasicInformationDto, 'zipCode'> & {
  zipCode: string;
};

/**
 * A component to collection the basic information for an organization.
 */
const BasicInformationStep = ({
  isStepComplete,
  setStepComplete,
  moveToNextStep,
  config,
  showError,
  isMasterServiceAgreementStepComplete,
}: CommonStepProps) => {
  const clientOnboardingApi = useApi(ClientOnboardingApi);
  const snackbarContext = useContext(SnackbarContext);
  const [isLoading, setIsLoading] = useState(true);

  const [initialValues, setInitialValues] = useState<BasicInformationFormDto>({
    legalOrganizationName: '',
    addressLineA: '',
    addressLineB: '',
    city: '',
    state: -1 as UsState,
    zipCode: '',
    phoneNumber: '',
  });

  useEffect(() => {
    // If the step is not complete, return early
    if (!isStepComplete) {
      setIsLoading(false);
      return;
    }

    // Load the basic information
    setIsLoading(true);
    clientOnboardingApi
      .GetBasicInformationStep()
      .then((b) => {
        setInitialValues({
          ...b,
          state: b.state,
          zipCode: b.zipCode.toString(),
        });
      })
      .catch(() => {
        snackbarContext.setAlert(
          <Alert severity="error" variant="filled">
            An error occurred while retrieving the previously submitted basic
            information.
          </Alert>
        );
        snackbarContext.triggerSnackbar();
        showError();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [clientOnboardingApi, isStepComplete, showError, snackbarContext]);

  const schema: ObjectSchema<BasicInformationFormDto> = object({
    legalOrganizationName: string().defined('Required'),
    addressLineA: string().defined('Required'),
    addressLineB: string(),
    city: string().defined('Required'),
    state: number().defined('Required'),
    zipCode: string()
      .matches(/^\d{5}$/, 'Zip code should be 5 numbers')
      .defined('Required'),
    phoneNumber: string()
      .matches(
        new RegExp(config.phoneNumberRegex),
        'Phone number should follow the format 123-456-7890'
      )
      .defined('Required'),
  });

  const formik = useFormik<BasicInformationFormDto>({
    initialValues: initialValues,
    validationSchema: isMasterServiceAgreementStepComplete ? undefined : schema,
    enableReinitialize: true,
    onSubmit: (val, { setSubmitting }) => {
      // Complete the step
      const basicInformationDto = val as unknown as BasicInformationDto;
      clientOnboardingApi
        .CompleteBasicInformationStep(basicInformationDto)
        .then(() => {
          if (!isStepComplete) {
            setStepComplete();
            moveToNextStep();
          } else {
            snackbarContext.setAlert(
              <Alert variant="filled" severity="success">
                Successfully updated basic information
              </Alert>
            );
            snackbarContext.triggerSnackbar();
          }
        })
        .catch(() => {
          snackbarContext.setAlert(
            <Alert variant="filled" severity="error">
              An error occurred while completing the basic information step.
              Please try again.
            </Alert>
          );
          snackbarContext.triggerSnackbar();
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  return (
    <Container
      maxWidth="md"
      sx={{ paddingBottom: '2em', position: 'relative', minHeight: '10em' }}
    >
      <Typography sx={{ marginBottom: '1.5em' }}>
        Completing this form is the first step towards establishing a safe
        environment for your organization. Your input will help us generate
        essential legal agreements and expedite your organization&apos;s setup
        in our e-learning system. We&apos;re here to support you every step of
        the way!
      </Typography>
      <Divider sx={{ marginBottom: '1em' }} />
      <form onSubmit={formik.handleSubmit}>
        <FormikTextField
          formik={formik}
          fullWidth
          label="Organization Legal Name"
          name="legalOrganizationName"
          autoFocus={!isStepComplete && !isLoading}
          autoComplete="organization"
          disabled={isMasterServiceAgreementStepComplete}
          sx={{
            marginBottom: '1em',
          }}
        />
        <FormikTextField
          formik={formik}
          fullWidth
          label="Address Line A"
          name="addressLineA"
          autoComplete="address-line1"
          disabled={isMasterServiceAgreementStepComplete}
          sx={{
            marginBottom: '1em',
          }}
        />
        <FormikTextField
          formik={formik}
          fullWidth
          label="(Optional) Address Line B"
          name="addressLineB"
          autoComplete="address-line2"
          disabled={isMasterServiceAgreementStepComplete}
          sx={{
            marginBottom: '1em',
          }}
        />
        <Box width="100%" display="flex" gap="1em">
          <FormikTextField
            formik={formik}
            label="City"
            name="city"
            autoComplete="address-level2"
            disabled={isMasterServiceAgreementStepComplete}
            sx={{
              width: '65%',
            }}
          />
          <FormikEnumSelect
            enum={UsState}
            formik={formik}
            label="State"
            name="state"
            disabled={isMasterServiceAgreementStepComplete}
            sx={{
              width: '35%',
            }}
          />
        </Box>
        <Box width="100%" display="flex" gap="1em">
          <FormikTextField
            formik={formik}
            label="Zip Code"
            name="zipCode"
            autoComplete="postal-code"
            disabled={isMasterServiceAgreementStepComplete}
            sx={{
              width: '50%',
              marginBottom: '1em',
            }}
          />
          <FormikTextField
            formik={formik}
            label="Phone Number"
            name="phoneNumber"
            autoComplete="tel"
            disabled={isMasterServiceAgreementStepComplete}
            sx={{
              width: '50%',
              marginBottom: '1em',
            }}
          />
        </Box>
        <Box
          display="flex"
          justifyContent="center"
          gap="1em"
          paddingBottom="1em"
        >
          {!isMasterServiceAgreementStepComplete && (
            <LoadingButton
              loading={formik.isSubmitting}
              type="submit"
              variant={isStepComplete ? 'outlined' : 'contained'}
            >
              {isStepComplete ? 'Update' : 'Submit'} Basic Information
            </LoadingButton>
          )}
          {isStepComplete && (
            <Button onClick={moveToNextStep} variant="contained">
              Next Step
            </Button>
          )}
        </Box>
      </form>
      <LoadingCircle isLoading={isLoading} blurEdges loadingDelayMs={0} />
    </Container>
  );
};

export default BasicInformationStep;
